import { useState } from "react";
import {
  ExternalLink,
  Lock,
  Minus,
  Pencil,
  Plus,
  Ruler,
  Scissors,
  Upload,
  Wand2,
  type LucideIcon,
} from "lucide-react";
import { CUSTOM_OPTION_SENTINEL } from "@shared-core/domain/catalogue-configuration";
import { StockListInput } from "./StockListInput";
import {
  isCountField,
  parseDimensionsWorkingValue,
} from "@shared-core/domain/catalogue-custom-value";
import {
  optionRequirementHint,
  unavailableOptionCodes,
} from "@shared-core/domain/catalogue-option-requirements";
import {
  isPaperTypeUnavailable,
  optionForPaperType,
  paperSplit,
} from "@shared-core/domain/catalogue-paper-choice";
import type {
  CatalogueField,
  CatalogueOption,
  CustomValueConstraints,
} from "@shared-core/data/catalogue-types";
import type { VersionsValue } from "@shared-core/domain/line-versions";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { cn } from "../../primitives/utils";
import { VersionsControl, type VersionsControlErrors } from "./VersionsControl";

// The item's specification: one row per field, each drawn by the control its
// mode asks for. Lifted from the catalogue item page so One's item
// configuration widget draws the same controls; the page and the widget each
// hold the selection and pass it in.

/** What the orderer has chosen for the item's fields: the configuration before it is built for the wire. */
export interface ItemSelection {
  /** A choice field's option code, or `CUSTOM_OPTION_SENTINEL` while its custom value is in use. */
  values: Record<string, string>;
  /** An always-custom field's value, or a choice-or-custom field's own value. */
  custom: Record<string, string>;
  /** A multi field's chosen option codes. */
  multi: Record<string, string[]>;
}

const ALWAYS_CUSTOM_ICON: Record<string, LucideIcon> = {
  dimension: Ruler,
  die: Scissors,
  variable_data: Wand2,
};

// ── Field controls ──────────────────────────────────────────────────────────

/**
 * Whether an option's `priceDelta` is actually added to the quote.
 *
 * Only for fields excluded from the variant cost matrix. A cost-affecting
 * field's option is already inside the cached manufacturing cost the price is
 * derived from, so its delta is not charged (see
 * `catalogue_module/actions/_option_charges.py`) — and a chip advertising
 * "+£8.00" for a charge the quote does not contain is the same class of lie as a
 * mock price sitting beside a real one.
 */
function isChargeableUpcharge(
  field: CatalogueField,
  opt: { priceDelta?: number | null },
): boolean {
  return field.affectsCost === false && Boolean(opt.priceDelta);
}

/**
 * Width × height pair + live area readout + the press-capability limit
 * (design doc "2c": "the press-capability limit shown inline"). Shared by
 * the always_custom dimension field (a bare panel size) and a
 * choice_or_custom field's "Other…" entry (a preset size plus a bespoke
 * one) — the control is identical either way, only the surrounding chips
 * differ. Stores the canonical `"{w} x {h}"` wire format (no unit suffix —
 * see `formatDimensionsValue`), never the display-only " mm".
 */
function DimensionsFields({
  value,
  onChange,
  constraints,
  ariaLabelPrefix,
}: {
  value: string;
  onChange: (raw: string) => void;
  constraints?: CustomValueConstraints;
  ariaLabelPrefix: string;
}) {
  const { width, height } = parseDimensionsWorkingValue(value);
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  const set = (nw: string, nh: string) => onChange(`${nw} x ${nh}`);
  const areaM2 = w > 0 && h > 0 ? (w * h) / 1_000_000 : 0;
  const dimInput =
    "w-24 px-3 py-1.5 rounded-md border border-border bg-card text-sm tabular-nums";
  const limitParts = [
    constraints?.max_width_mm != null
      ? `max ${constraints.max_width_mm}mm wide`
      : null,
    constraints?.max_depth_mm != null
      ? `max ${constraints.max_depth_mm}mm deep`
      : null,
  ].filter((p): p is string => p != null);
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <input
        type="number"
        min={1}
        value={width}
        onChange={(e) => set(e.target.value, height)}
        placeholder="Width"
        aria-label={`${ariaLabelPrefix} width in mm`}
        className={dimInput}
      />
      <span className="text-muted-foreground">×</span>
      <input
        type="number"
        min={1}
        value={height}
        onChange={(e) => set(width, e.target.value)}
        placeholder="Height"
        aria-label={`${ariaLabelPrefix} height in mm`}
        className={dimInput}
      />
      <span className="text-muted-foreground">mm</span>
      {areaM2 > 0 && (
        <span className="text-xs text-muted-foreground">
          area {areaM2.toFixed(2)} m²
        </span>
      )}
      {limitParts.length > 0 && (
        <span className="text-xs text-muted-foreground">
          · {limitParts.join(" · ")}
        </span>
      )}
    </div>
  );
}

/**
 * The control for a `choice_or_custom` field's "Other…" entry, fit to
 * `customValueType` (design doc "2c"):
 *   integer    — a numeric input honouring the field's own step/min/max.
 *   dimensions — width × height + area + capability limit (`DimensionsFields`).
 *   text       — a plain input: a typed value, such as a paper the dictionary
 *                does not list ("Other stock, quote on request"), which the
 *                line then waits on supplier quotes for; a paper field with a
 *                stock list searches it as you type (`StockListInput`).
 * Falls back to the plain text input for a field with no declared type —
 * matches the previous behaviour, though the backend rejects such a value.
 */
function CustomValueOtherInput({
  field,
  value,
  onChange,
}: {
  field: CatalogueField;
  value: string;
  onChange: (raw: string) => void;
}) {
  if (field.customValueType === "dimensions") {
    return (
      <DimensionsFields
        value={value}
        onChange={onChange}
        constraints={field.customConstraints}
        ariaLabelPrefix={field.label}
      />
    );
  }
  if (field.customValueType === "integer") {
    return (
      <input
        autoFocus
        type="number"
        step={field.step ?? 1}
        min={field.min}
        max={field.max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.customHint ?? "Enter a number"}
        className="px-3 py-1.5 rounded-md border border-border bg-card text-sm w-32 tabular-nums"
      />
    );
  }
  if (field.customValueType === "text" && field.stockListUrl) {
    return (
      <StockListInput
        field={field}
        stockListUrl={field.stockListUrl}
        value={value}
        onChange={onChange}
      />
    );
  }
  return (
    <input
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={field.customLabel ?? `${field.label}, other`}
      maxLength={field.customConstraints?.max_length}
      placeholder={field.customHint ?? "Enter a value"}
      className="px-3 py-1.5 rounded-md border border-border bg-card text-sm w-full max-w-md"
    />
  );
}

function ChipRow({
  field,
  value,
  customValue,
  onPick,
  onCustom,
  error,
  unavailableCodes,
  requirementHint,
}: {
  field: CatalogueField;
  unavailableCodes?: ReadonlySet<string>;
  requirementHint?: (option: CatalogueOption) => string | undefined;
  value: string;
  customValue: string;
  onPick: (code: string) => void;
  onCustom: (raw: string) => void;
  error?: string;
}) {
  const isCustom = value === CUSTOM_OPTION_SENTINEL;
  // Paper reads as its type, then the chosen type's weights (R204); the value is still one option code.
  const split = paperSplit(field.options ?? [], value);
  // A typed paper ("Other stock, quote on request") is another type, not a weight; a custom weight would be a weight.
  const customIsAType = field.customValueType === "text";
  const optionChip = (opt: CatalogueOption, label: string) => {
    const unavailable = unavailableCodes?.has(opt.code) ?? false;
    return (
      <button
        key={opt.code}
        onClick={() => onPick(opt.code)}
        disabled={unavailable}
        title={unavailable ? requirementHint?.(opt) : undefined}
        className={cn(
          chipClass(value === opt.code),
          unavailable && "cursor-not-allowed opacity-40",
        )}
      >
        {label}
        {isChargeableUpcharge(field, opt) ? (
          <span className="ml-1 opacity-70">+£{opt.priceDelta}</span>
        ) : null}
      </button>
    );
  };
  const customChoice = (
    <>
      {field.mode === "choice_or_custom" && (
        <button
          onClick={() => onPick(CUSTOM_OPTION_SENTINEL)}
          className={chipClass(isCustom)}
        >
          {field.customLabel ?? "Other…"}
        </button>
      )}
      {isCustom && (
        <CustomValueOtherInput
          field={field}
          value={customValue}
          onChange={onCustom}
        />
      )}
    </>
  );
  return (
    <div className="space-y-1.5">
      {split ? (
        <>
          <div
            role="group"
            aria-label={`${field.label} type`}
            className="flex flex-wrap items-center gap-2"
          >
            {split.types.map((type) => (
              <button
                key={type.code}
                onClick={() =>
                  onPick(
                    optionForPaperType(
                      type,
                      field.options?.find((o) => o.code === value),
                      unavailableCodes,
                    ).code,
                  )
                }
                disabled={isPaperTypeUnavailable(type, unavailableCodes)}
                className={cn(
                  chipClass(split.selectedType?.code === type.code),
                  "disabled:cursor-not-allowed disabled:opacity-40",
                )}
              >
                {type.label}
              </button>
            ))}
            {customIsAType && customChoice}
          </div>
          {/* A named stock is its own weight: there is none to pick. */}
          {(split.selectedType?.isNamedStock === false ||
            (field.mode === "choice_or_custom" && !customIsAType)) && (
            <div
              role="group"
              aria-label={`${field.label} weight`}
              className="flex flex-wrap items-center gap-2"
            >
              {split.selectedType?.isNamedStock === false &&
                split.selectedType.options.map((opt) =>
                  optionChip(opt, opt.paper!.weightLabel),
                )}
              {!customIsAType && customChoice}
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {field.options?.map((opt) => optionChip(opt, opt.label))}
          {customChoice}
        </div>
      )}
      {isCustom && error && (
        <div className="text-xs text-[color:var(--color-error,red)]">
          {error}
        </div>
      )}
      {isCustom && !error && field.customHint && (
        <div className="text-xs text-muted-foreground">{field.customHint}</div>
      )}
    </div>
  );
}

function MultiRow({
  field,
  selected,
  onToggle,
}: {
  field: CatalogueField;
  selected: string[];
  onToggle: (code: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {field.options?.map((opt) => (
        <button
          key={opt.code}
          onClick={() => onToggle(opt.code)}
          className={chipClass(selected.includes(opt.code))}
        >
          + {opt.label}
          {isChargeableUpcharge(field, opt) ? (
            <span className="ml-1 opacity-70">£{opt.priceDelta}</span>
          ) : null}
        </button>
      ))}
      {field.options?.length === 0 && (
        <span className="text-sm text-muted-foreground">None available</span>
      )}
    </div>
  );
}

/**
 * A constrained numeric stepper (e.g. pages in 4s), clamped so an invalid
 * value can't be entered in the first place.
 *
 * A `choice` field — or a `choice_or_custom` field with no `customValueType`
 * wired, i.e. no real live-costing escape hatch — may only ever land on one
 * of its own preset option codes: `min`/`max`/`step` describe the full
 * custom range of a *properly wired* choice_or_custom field, and stepping
 * through that raw arithmetic range for a field with no such wiring would
 * let the orderer land on an off-preset value nothing can ever price. Before
 * this guard, that off-preset value silently priced off a DIFFERENT preset's
 * cached cost (the brochure Pages=20 incident, est 653778 — see
 * `catalogue_module/actions/generate_dictionary_item_variants.py`'s
 * `_project_cost_affecting_configuration` for the backend half of this
 * invariant); with that backend fix alone it would instead dead-end as an
 * honest but pointless "price unavailable" for a value that was never
 * reachable in the first place. Stepping through the field's own sorted
 * preset codes keeps every reachable value priceable.
 */
function StepperRow({
  field,
  value,
  onChange,
  error,
}: {
  field: CatalogueField;
  value: string;
  onChange: (raw: string) => void;
  /** Custom-value constraint violation for the field's active off-preset
   *  value, if any — see `resolveCustomValueLadderConfiguration`. */
  error?: string;
}) {
  const presetOnly =
    field.mode === "choice" ||
    (field.mode === "choice_or_custom" && field.customValueType == null);
  const presetValues = presetOnly
    ? Array.from(new Set((field.options ?? []).map((o) => Number(o.code))))
        .filter((n) => Number.isFinite(n))
        .sort((a, b) => a - b)
    : null;

  const step = field.step ?? 1;
  const min = presetValues ? (presetValues[0] ?? 0) : (field.min ?? 0);
  const max = presetValues
    ? (presetValues[presetValues.length - 1] ?? min)
    : (field.max ?? Number.MAX_SAFE_INTEGER);
  const current =
    parseInt(value || field.defaultValue || String(min), 10) || min;

  const stepTo = (from: number, direction: 1 | -1): number => {
    if (!presetValues || presetValues.length === 0) {
      return Math.min(max, Math.max(min, from + direction * step));
    }
    const idx = presetValues.indexOf(from);
    if (idx !== -1) {
      const nextIdx = Math.min(
        Math.max(idx + direction, 0),
        presetValues.length - 1,
      );
      return presetValues[nextIdx] ?? from;
    }
    // `from` isn't itself a preset (a stale value from before this field
    // was narrowed to presets-only) — snap to the nearest preset in the
    // requested direction.
    const candidates =
      direction > 0
        ? presetValues.filter((v) => v > from)
        : [...presetValues].filter((v) => v < from).reverse();
    return (
      candidates[0] ??
      (direction > 0
        ? presetValues[presetValues.length - 1]
        : presetValues[0]) ??
      from
    );
  };
  const set = (n: number) => onChange(String(n));
  const stepClass =
    "flex size-8 items-center justify-center rounded-md border border-border text-muted-foreground hover:border-[color:var(--color-primary)] disabled:opacity-40 disabled:hover:border-border";
  return (
    <div className="flex items-center gap-2">
      <div className="inline-flex items-center gap-1.5">
        <button
          onClick={() => set(stepTo(current, -1))}
          disabled={current <= min}
          className={stepClass}
          aria-label="Decrease"
        >
          <Minus className="size-3.5" />
        </button>
        <span className="w-12 text-center text-sm font-medium tabular-nums">
          {current}
        </span>
        <button
          onClick={() => set(stepTo(current, 1))}
          disabled={current >= max}
          className={stepClass}
          aria-label="Increase"
        >
          <Plus className="size-3.5" />
        </button>
      </div>
      {error ? (
        <span className="text-xs text-[color:var(--color-error,red)]">
          {error}
        </span>
      ) : (
        field.customHint && (
          <span className="text-xs text-muted-foreground">
            {field.customHint}
          </span>
        )
      )}
    </div>
  );
}

/**
 * What the count's version control needs beyond the selection: the split and quantities the line carries, and the
 * quantity they share. The count itself is the selection's `Versions` field (`selection.custom`).
 */
export interface LineVersions {
  value: VersionsValue;
  /** The line's quantity in all, shared between the versions. */
  quantity: number;
  /** The step the item's quantities move in. */
  quantityStep: number;
  /** The backend's field errors for the count, the split and the quantities, bound beside their inputs. */
  errors?: VersionsControlErrors;
  /** The count was typed, or the split or a version's quantity changed. */
  onChange: (next: VersionsValue) => void;
}

interface ControlProps {
  field: CatalogueField;
  selection: ItemSelection;
  onChange: (next: Partial<ItemSelection>) => void;
  lineVersions?: LineVersions;
  /** Local constraint-violation message for this field's active custom
   *  value, if any — see `resolveCustomValueLadderConfiguration`. */
  error?: string;
}

// always_custom fields each get a control fit to their shape: a dimension is two
// numeric inputs, a die/forme is a small "saved / template / upload" chooser, and
// variable-data hands off to the online editor (mocked as a routing dialog).
function AlwaysCustomControl({
  field,
  selection,
  onChange,
  error,
  lineVersions,
}: ControlProps) {
  if (isCountField(field) && lineVersions)
    return (
      <CountControl
        field={field}
        selection={selection}
        onChange={onChange}
        lineVersions={lineVersions}
      />
    );
  if (field.control === "dimension")
    return (
      <DimensionControl
        field={field}
        selection={selection}
        onChange={onChange}
        error={error}
      />
    );
  if (field.control === "die")
    return (
      <DieControl field={field} selection={selection} onChange={onChange} />
    );
  if (field.control === "variable_data")
    return <OnlineEditorControl field={field} />;
  if (field.customValueType === "text")
    return (
      <TextControl
        field={field}
        selection={selection}
        onChange={onChange}
        error={error}
      />
    );
  return <SetupStub field={field} />;
}

// A typed value: a description as quoted, or a number kept as text (an NCR
// start number, whose leading zeros must survive). Blank is no value.
function TextControl({ field, selection, onChange, error }: ControlProps) {
  const hintId = `${field.key}-hint`;
  const isNumber = field.customConstraints?.digits_only === true;
  return (
    <div className="space-y-1.5">
      <input
        value={selection.custom[field.key] ?? ""}
        onChange={(e) =>
          onChange({
            custom: { ...selection.custom, [field.key]: e.target.value },
          })
        }
        inputMode={isNumber ? "numeric" : undefined}
        aria-label={field.label}
        aria-invalid={error ? true : undefined}
        aria-describedby={hintId}
        maxLength={field.customConstraints?.max_length}
        className={cn(
          "px-3 py-1.5 rounded-md border border-border bg-card text-sm",
          isNumber ? "w-40 tabular-nums" : "w-full",
        )}
      />
      {error ? (
        <div
          id={hintId}
          className="text-xs text-[color:var(--color-error,red)]"
        >
          {error}
        </div>
      ) : (
        field.customHint && (
          <div id={hintId} className="text-xs text-muted-foreground">
            {field.customHint}
          </div>
        )
      )}
    </div>
  );
}

// Fallback for any future always_custom control with no bespoke UI yet.
function SetupStub({ field }: { field: CatalogueField }) {
  const Icon = ALWAYS_CUSTOM_ICON[field.control ?? ""] ?? Pencil;
  return (
    <div className="flex items-start gap-3 rounded-md border border-dashed border-border bg-muted p-3">
      <Icon className="size-5 text-muted-foreground shrink-0 mt-0.5" />
      <div className="space-y-1">
        <div className="text-sm font-medium">
          Set up {field.label.toLowerCase()}
        </div>
        {field.customHint && (
          <div className="text-xs text-muted-foreground">
            {field.customHint}
          </div>
        )}
      </div>
    </div>
  );
}

// A count (Versions): how many artworks share this spec, with how the quantity is shared between them. It never
// changes the variant, so the count is never live-costed; the quantity stays the total across the count. A count of 1
// is no count (the backend's rule), so the configuration carries it only once it differs: an untouched or reset count
// sends nothing.
function CountControl({
  field,
  selection,
  onChange,
  lineVersions,
}: Omit<ControlProps, "error"> & { lineVersions: LineVersions }) {
  const minimum = String(field.min);
  return (
    <div className="space-y-1.5">
      <VersionsControl
        label={field.label}
        countRaw={selection.custom[field.key] ?? minimum}
        value={lineVersions.value}
        quantity={lineVersions.quantity}
        quantityStep={lineVersions.quantityStep}
        errors={lineVersions.errors}
        idPrefix={`${field.key}-versions`}
        onCountChange={(raw, next) => {
          const custom = { ...selection.custom, [field.key]: raw };
          if (raw === minimum) delete custom[field.key];
          onChange({ custom });
          lineVersions.onChange(next);
        }}
        onValueChange={lineVersions.onChange}
      />
      {field.customHint && (
        <div className="text-xs text-muted-foreground">{field.customHint}</div>
      )}
    </div>
  );
}

// A flat panel only varies by width × height. `DimensionsFields` renders the
// two inputs + live area readout + capability limit; persisted in the
// canonical "{w} x {h}" wire format (no unit suffix) so it parses on the
// backend unchanged.
function DimensionControl({ field, selection, onChange, error }: ControlProps) {
  return (
    <div className="space-y-1.5">
      <DimensionsFields
        value={selection.custom[field.key] ?? ""}
        onChange={(next) =>
          onChange({ custom: { ...selection.custom, [field.key]: next } })
        }
        constraints={field.customConstraints}
        ariaLabelPrefix={field.label}
      />
      {error ? (
        <div className="text-xs text-[color:var(--color-error,red)]">
          {error}
        </div>
      ) : (
        field.customHint && (
          <div className="text-xs text-muted-foreground">
            {field.customHint}
          </div>
        )
      )}
    </div>
  );
}

// A die/forme is a cut-and-crease shape, not a rectangle. It's specified one of
// three ways — reuse a held die, adapt a standard template, or supply a die-line.
const MOCK_SAVED_DIES = [
  "A4 folder · business-card slits",
  "A4 folder · CD slot",
  "A5 folder · no slits",
];
const MOCK_DIE_TEMPLATES = [
  "A4 capacity, 4pp",
  "A4 capacity, 6pp gatefold",
  "A5 capacity, 4pp",
];

function DieControl({ field, selection, onChange }: ControlProps) {
  const mode = selection.values[field.key] ?? "";
  const detail = selection.custom[field.key] ?? "";
  const setMode = (m: string) =>
    onChange({
      values: { ...selection.values, [field.key]: m },
      custom: { ...selection.custom, [field.key]: "" },
    });
  const setDetail = (summary: string) =>
    onChange({ custom: { ...selection.custom, [field.key]: summary } });
  const modes = [
    { key: "saved", label: "Reuse a saved die" },
    { key: "template", label: "Start from a template" },
    { key: "upload", label: "Upload a die-line" },
  ];
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {modes.map((m) => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            className={chipClass(mode === m.key)}
          >
            {m.label}
          </button>
        ))}
      </div>
      {mode === "saved" && (
        <div className="flex flex-wrap gap-2">
          {MOCK_SAVED_DIES.map((d) => (
            <button
              key={d}
              onClick={() => setDetail(d)}
              className={chipClass(detail === d)}
            >
              {d}
            </button>
          ))}
        </div>
      )}
      {mode === "template" && (
        <div className="flex flex-wrap gap-2">
          {MOCK_DIE_TEMPLATES.map((t) => (
            <button
              key={t}
              onClick={() => setDetail(`Template: ${t}`)}
              className={chipClass(detail === `Template: ${t}`)}
            >
              {t}
            </button>
          ))}
        </div>
      )}
      {mode === "upload" && (
        <div className="space-y-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDetail("Die-line uploaded")}
          >
            <Upload className="size-4 mr-1" /> Upload die-line
          </Button>
          <div className="text-xs text-muted-foreground">
            {detail === "Die-line uploaded"
              ? "die-line.pdf attached"
              : "PDF or vector keyline showing cut & crease"}
          </div>
        </div>
      )}
    </div>
  );
}

// Variable-data items are personalised in the online editor — a hand-off, not an
// in-page field. Render an action button; the dialog mocks the routing.
function OnlineEditorControl({ field }: { field: CatalogueField }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Wand2 className="size-4 mr-1" /> Personalise online
        <ExternalLink className="size-3.5 ml-1.5 opacity-70" />
      </Button>
      {field.customHint && (
        <span className="text-xs text-muted-foreground">
          {field.customHint}
        </span>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Online editor</DialogTitle>
            <DialogDescription>
              The user would be routed to the Chili online editor to set up this
              item, then returned here to place the order.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// All selectable chips share one subtle "selected" treatment — a pink outline
// over a faint tint (matching the quantity table + delivery rows). Solid primary
// pink is reserved for actual CTAs.
function chipClass(active: boolean): string {
  return `px-3 py-1.5 rounded-md text-sm border ${
    active
      ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/5 text-foreground font-medium"
      : "bg-card text-foreground border-border hover:border-[color:var(--color-primary)]"
  }`;
}

export function ItemFieldRow({
  field,
  selection,
  onChange,
  error,
  reorderChip,
  unavailableCodes,
  requirementHint,
  lineVersions,
}: {
  field: CatalogueField;
  selection: ItemSelection;
  onChange: (next: Partial<ItemSelection>) => void;
  /** The version control's split, quantities and errors, for the count field. */
  lineVersions?: LineVersions;
  /** This field's active custom-value constraint violation, if any. */
  error?: string;
  /** Option codes the rest of the selection rules out (R28), shown disabled. */
  unavailableCodes?: ReadonlySet<string>;
  /** Why an option is unavailable, for its tooltip. */
  requirementHint?: (option: CatalogueOption) => string | undefined;
  /** Order again pre-fill confidence chip (catalogue-reorder brief §16) — absent
   *  outside a reorder landing, or for a tier-"preset" field (indistinguishable
   *  from manual by design). See `ReorderFieldChip`. */
  reorderChip?: React.ReactNode;
}) {
  const opts = field.options ?? [];

  // Locked language (a lock glyph + muted value, no interactive affordance) for
  // anything with no real choice: a pinned value, or a single-option choice
  // (one board/stock — nothing to pick). choice_or_custom keeps its escape.
  const lockedValue =
    field.mode === "fixed"
      ? field.fixedValue
      : field.mode === "choice" &&
          field.control !== "multi" &&
          opts.length === 1
        ? opts[0].label
        : null;

  if (lockedValue) {
    return (
      <FieldShell label={field.label}>
        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
          <Lock className="size-3.5 text-muted-foreground" />
          {lockedValue}
        </span>
      </FieldShell>
    );
  }

  const control = (() => {
    if (field.mode === "always_custom")
      return (
        <AlwaysCustomControl
          field={field}
          selection={selection}
          onChange={onChange}
          error={error}
          lineVersions={lineVersions}
        />
      );
    if (field.control === "multi") {
      const selected = selection.multi[field.key] ?? [];
      const setMulti = (next: string[]) =>
        onChange({ multi: { ...selection.multi, [field.key]: next } });
      // A single optional extra reads clearest as an explicit No / Yes pair.
      if (opts.length === 1) {
        const opt = opts[0];
        const on = selected.includes(opt.code);
        return (
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setMulti([])} className={chipClass(!on)}>
              No {opt.label.toLowerCase()}
            </button>
            <button
              onClick={() => setMulti([opt.code])}
              className={chipClass(on)}
            >
              {opt.label}
              {isChargeableUpcharge(field, opt) ? (
                <span className="ml-1 opacity-70">+£{opt.priceDelta}</span>
              ) : null}
            </button>
          </div>
        );
      }
      return (
        <MultiRow
          field={field}
          selected={selected}
          onToggle={(code) => {
            const next = selected.includes(code)
              ? selected.filter((c) => c !== code)
              : [...selected, code];
            setMulti(next);
          }}
        />
      );
    }
    if (field.control === "number") {
      // A genuinely-wired choice_or_custom field (customValueType set —
      // e.g. brochure Pages) reuses the SAME CUSTOM_OPTION_SENTINEL/
      // selection.custom convention ChipRow's "Other…" chip uses, so this
      // stepper feeds the same custom-value ladder (resolveCustomValueLadderConfiguration
      // / useCatalogueCustomValueLadder) without a second parallel
      // mechanism. Landing on a preset value clears back to a plain
      // selection; landing off-preset marks the field custom.
      const customWired =
        field.mode === "choice_or_custom" && field.customValueType != null;
      const isCustomActive =
        customWired && selection.values[field.key] === CUSTOM_OPTION_SENTINEL;
      return (
        <StepperRow
          field={field}
          value={
            isCustomActive
              ? (selection.custom[field.key] ?? "")
              : (selection.values[field.key] ?? "")
          }
          onChange={(raw) => {
            const isPreset = (field.options ?? []).some((o) => o.code === raw);
            if (customWired && !isPreset) {
              onChange({
                values: {
                  ...selection.values,
                  [field.key]: CUSTOM_OPTION_SENTINEL,
                },
                custom: { ...selection.custom, [field.key]: raw },
              });
            } else {
              onChange({ values: { ...selection.values, [field.key]: raw } });
            }
          }}
          error={error}
        />
      );
    }
    return (
      <ChipRow
        field={field}
        unavailableCodes={unavailableCodes}
        requirementHint={requirementHint}
        value={selection.values[field.key] ?? ""}
        customValue={selection.custom[field.key] ?? ""}
        onPick={(code) =>
          onChange({ values: { ...selection.values, [field.key]: code } })
        }
        onCustom={(raw) =>
          onChange({ custom: { ...selection.custom, [field.key]: raw } })
        }
        error={error}
      />
    );
  })();

  return (
    <FieldShell label={field.label}>
      {control}
      {reorderChip ? <div className="mt-1.5">{reorderChip}</div> : null}
    </FieldShell>
  );
}

// A dense spec-sheet row: label on the left, control on the right.
function FieldShell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:gap-4">
      <div className="shrink-0 text-sm text-muted-foreground sm:w-28">
        {label}
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/**
 * A compact "85 × 55 mm · Full colour · 350gsm" recap of the chosen spec.
 * Job-level choices plus any section choice the orderer can actually change —
 * locked single-option fields (boards, stocks) are not repeated in the
 * summary, so it doesn't bloat. Section entries are qualified ("Cover: …") so
 * cover vs text print don't read as duplicates.
 */
export function specRecap(
  fields: CatalogueField[],
  selection: ItemSelection,
): string {
  return fields
    .filter((f) => {
      if (!f.section) return true;
      if (f.control === "number" || f.mode === "choice_or_custom") return true;
      return (f.options?.length ?? 0) > 1;
    })
    .map((f) => ({ f, v: summarise(f, selection) }))
    .filter(({ v }) => v && v !== "None" && v !== "—" && v !== "To set up")
    .map(({ f, v }) => (f.section ? `${f.section}: ${v}` : v))
    .join(" · ");
}

function summarise(field: CatalogueField, selection: ItemSelection): string {
  if (field.mode === "fixed") return field.fixedValue ?? "—";
  if (field.mode === "always_custom")
    return (
      selection.custom[field.key] ||
      (isCountField(field) ? String(field.min) : "To set up")
    );
  if (field.control === "multi") {
    const sel = selection.multi[field.key] ?? [];
    if (sel.length === 0) return "None";
    return sel
      .map((c) => field.options?.find((o) => o.code === c)?.label ?? c)
      .join(", ");
  }
  if (field.control === "number") {
    const raw = selection.values[field.key];
    if (raw === CUSTOM_OPTION_SENTINEL)
      return selection.custom[field.key] || "Custom";
    return raw || field.defaultValue || "—";
  }
  const v = selection.values[field.key];
  if (v === CUSTOM_OPTION_SENTINEL)
    return selection.custom[field.key] || "Custom";
  return field.options?.find((o) => o.code === v)?.label ?? "—";
}

/**
 * The item's specification sheet: its job-level fields, then each named
 * section's (a multi-part item's cover and text). Options the rest of the
 * selection rules out (R28) are greyed with why; `errors` are each field's
 * custom-value constraint violation, by field key.
 */
export function ItemSpecification({
  fields,
  selection,
  onChange,
  errors,
  reorderChipFor,
  lineVersions,
}: {
  fields: CatalogueField[];
  selection: ItemSelection;
  onChange: (next: Partial<ItemSelection>) => void;
  errors: Record<string, string | undefined>;
  /** The line's versions beyond the count: how its quantity is shared. The item's count field draws the control. */
  lineVersions: LineVersions;
  /** A reorder landing's confidence chip for a field, beside its control. */
  reorderChipFor?: (fieldKey: string) => React.ReactNode;
}) {
  const jobFields = fields.filter((f) => !f.section);
  const unavailableByField = unavailableOptionCodes(fields, selection.values);
  const sectionNames = fields
    .map((f) => f.section)
    .filter((s, i, arr): s is string => !!s && arr.indexOf(s) === i);
  const row = (f: CatalogueField) => (
    <ItemFieldRow
      key={f.key}
      field={f}
      selection={selection}
      onChange={onChange}
      error={errors[f.key]}
      lineVersions={lineVersions}
      unavailableCodes={unavailableByField.get(f.key)}
      requirementHint={(opt) => optionRequirementHint(opt, fields)}
      reorderChip={reorderChipFor?.(f.key)}
    />
  );
  return (
    <>
      <div className="text-sm font-medium">Specification</div>
      {jobFields.length > 0 && (
        <div className="divide-y divide-border">{jobFields.map(row)}</div>
      )}
      {sectionNames.map((name) => (
        <div key={name} className="mt-1 border-t border-border pt-2">
          <div className="py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {name}
          </div>
          <div className="divide-y divide-border">
            {fields.filter((f) => f.section === name).map(row)}
          </div>
        </div>
      ))}
    </>
  );
}
